
import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Navbar from './Navbar';
import ErrorBoundary from './ui/error-boundary';
import { ChunkLoadTracker } from '@/utils/bundleOptimizations';

// Enhanced lazy loading with chunk tracking
const chunkTracker = ChunkLoadTracker.getInstance();

const createLazyComponent = (importFunc: () => Promise<any>, chunkName: string) => 
  React.lazy(() => chunkTracker.trackChunkLoad(chunkName, importFunc()));

// Core pages - highest priority
const Index = createLazyComponent(() => import('@/pages/Index'), 'page-index');
const Contact = createLazyComponent(() => import('@/pages/Contact'), 'page-contact');
const Reviews = createLazyComponent(() => import('@/pages/Reviews'), 'page-reviews');

// Secondary pages - medium priority
const About = createLazyComponent(() => import('@/pages/About'), 'page-about');
const Suite = createLazyComponent(() => import('@/pages/Suite'), 'page-suite');

// Blog pages - lower priority (heavy content)
const Blog = createLazyComponent(() => import('@/pages/Blog'), 'page-blog');
const LoenaJournal = createLazyComponent(() => import('@/pages/LoenaJournal'), 'page-journal');
const JournalPost = createLazyComponent(() => import('@/pages/JournalPost'), 'page-journal-post');
const JournalArticle = createLazyComponent(() => import('@/pages/JournalArticle'), 'page-journal-article');

// Legal pages - lowest priority
const FAQ = createLazyComponent(() => import('@/pages/FAQ'), 'page-faq');
const PrivacyPolicy = createLazyComponent(() => import('@/pages/PrivacyPolicy'), 'page-privacy');
const CookiePolicy = createLazyComponent(() => import('@/pages/CookiePolicy'), 'page-cookie-policy');
const RentalConditions = createLazyComponent(() => import('@/pages/RentalConditions'), 'page-rental');

// Admin pages - on-demand only
const DSAR = createLazyComponent(() => import('@/pages/DSAR'), 'page-dsar');
const ImageAdmin = createLazyComponent(() => import('@/pages/ImageAdmin'), 'page-image-admin');
const Admin = createLazyComponent(() => import('@/pages/Admin'), 'page-admin');

// Utility pages
const NewsletterVerified = createLazyComponent(() => import('@/pages/NewsletterVerified'), 'page-newsletter-verified');
const NotFound = createLazyComponent(() => import('@/pages/NotFound'), 'page-not-found');

const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-loena-gold"></div>
  </div>
);

const AppRoutes = () => {
  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/rooms" element={<Suite />} />
              <Route path="/suite/:id" element={<Suite />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/loena-journal" element={<LoenaJournal />} />
              <Route path="/journal" element={<LoenaJournal />} />
              <Route path="/journal/:slug" element={<JournalPost />} />
              <Route path="/loena-journal/:slug" element={<JournalPost />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/rental-conditions" element={<RentalConditions />} />
              <Route path="/dsar" element={<DSAR />} />
              <Route path="/newsletter-verified" element={<NewsletterVerified />} />
              <Route path="/image-admin" element={<ImageAdmin />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </ErrorBoundary>
  );
};

export default AppRoutes;
